import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { useLanguage } from '@/contexts/LanguageContext';

interface ChannelRow {
  channel: string;
  applied: number;
  hired: number;
  hireRate: number | null;
}

const COLORS = [
  'hsl(221, 83%, 53%)',
  'hsl(142, 71%, 45%)',
  'hsl(38, 92%, 50%)',
  'hsl(25, 95%, 53%)',
  'hsl(270, 67%, 47%)',
  'hsl(210, 100%, 45%)',
  'hsl(0, 84%, 60%)',
  'hsl(170, 75%, 41%)',
];

const RADIAN = Math.PI / 180;

const renderCustomLabel = (props: {
  cx: number; cy: number; midAngle: number;
  outerRadius: number; channel: string; applied: number;
  percent: number; index: number;
}) => {
  const { cx, cy, midAngle, outerRadius, channel, percent } = props;
  const radius = outerRadius + 20;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const lineX = cx + (outerRadius + 6) * Math.cos(-midAngle * RADIAN);
  const lineY = cy + (outerRadius + 6) * Math.sin(-midAngle * RADIAN);
  const textAnchor = x > cx ? 'start' : 'end';

  if (percent < 0.05) return null;

  return (
    <g>
      <line x1={lineX} y1={lineY} x2={x} y2={y} stroke="hsl(240, 4%, 46%)" strokeWidth={1} />
      <text x={x + (x > cx ? 4 : -4)} y={y} textAnchor={textAnchor} dominantBaseline="central" fontSize={11} fill="hsl(240, 4%, 46%)">
        {channel} ({(percent * 100).toFixed(0)}%)
      </text>
    </g>
  );
};

export const ChannelChart = ({ data, className }: { data: ChannelRow[]; className?: string }) => {
  const { t } = useLanguage();

  if (data.length === 0) {
    return (
      <Card className="border-border/50">
        <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">{t('dashboard.applicationsByChannel')}</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-muted-foreground text-center py-8">{t('dashboard.noData')}</p></CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-border/50 ${className || ''}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">{t('dashboard.applicationsByChannel')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="applied"
                nameKey="channel"
                cx="50%"
                cy="50%"
                outerRadius={80}
                innerRadius={40}
                label={renderCustomLabel}
                labelLine={false}
                animationDuration={400}
                animationEasing="ease-out"
              >
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip
                content={({ payload }) => {
                  if (!payload?.[0]) return null;
                  const d = payload[0].payload as ChannelRow;
                  const total = data.reduce((s, r) => s + r.applied, 0);
                  const pct = total > 0 ? ((d.applied / total) * 100).toFixed(1) : '0';
                  return (
                    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-md">
                      <p className="font-medium">{d.channel}</p>
                      <p className="text-muted-foreground">{t('dashboard.applied')}: {d.applied} ({pct}%)</p>
                      <p className="text-muted-foreground">{t('dashboard.hired')}: {d.hired}</p>
                      {d.hireRate !== null
                        ? <p className="text-muted-foreground">{t('dashboard.hireRate')}: {d.hireRate.toFixed(1)}%</p>
                        : <p className="text-muted-foreground italic">{t('dashboard.hireRate')}: {t('dashboard.insufficientData')} (&lt;5)</p>
                      }
                    </div>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
